import { AssociationType } from "@/shared/domain/association";
import { FileRecord } from "@/shared/domain/files";
import { MessagePathNode, PathParser } from "@/shared/domain/topics";
import { PlotSeries } from "@/shared/state/visualization";

interface QualifiedFile {
  fileId: string;
  segments: string[];
  trailingSegmentCount: number;
}

/**
 * The trailing `relative_path` segments that tell each file apart, keyed by
 * file id. Every file starts at its basename; as long as files with different
 * paths share a qualifier, each of those qualifiers that can still grow gains
 * one more leading segment, so colliding `data.mcap`s become
 * `2026-07-01/data.mcap` and `2026-07-02/data.mcap` while unique basenames stay
 * short. Files sharing one `relative_path` end up with identical qualifiers; no
 * amount of path tells them apart.
 */
function distinguishingQualifiers(files: FileRecord[]): Map<string, string> {
  const uniqueFiles = new Map(files.map((file) => [file.file_id, file]));
  const qualifiedFiles: QualifiedFile[] = [...uniqueFiles.values()].map(
    (file) => ({
      fileId: file.file_id,
      segments: file.relative_path
        .split("/")
        .filter((segment) => segment.length > 0),
      trailingSegmentCount: 1,
    }),
  );

  const qualifierOf = (qualified: QualifiedFile): string =>
    qualified.segments.slice(-qualified.trailingSegmentCount).join("/");

  for (;;) {
    const filesByQualifier = new Map<string, QualifiedFile[]>();
    for (const qualified of qualifiedFiles) {
      const qualifier = qualifierOf(qualified);
      const bucket = filesByQualifier.get(qualifier);
      if (bucket === undefined) {
        filesByQualifier.set(qualifier, [qualified]);
      } else {
        bucket.push(qualified);
      }
    }

    let anyQualifierGrew = false;
    for (const bucket of filesByQualifier.values()) {
      // Distinct files can share one `relative_path`. Growing a bucket of those
      // lengthens their qualifiers without ever splitting the bucket, so only
      // differing paths drive growth.
      const distinctPaths = new Set(
        bucket.map((qualified) => qualified.segments.join("/")),
      );
      if (distinctPaths.size < 2) {
        continue;
      }
      for (const qualified of bucket) {
        if (qualified.trailingSegmentCount < qualified.segments.length) {
          qualified.trailingSegmentCount += 1;
          anyQualifierGrew = true;
        }
      }
    }
    if (!anyQualifierGrew) {
      return new Map(
        qualifiedFiles.map((qualified) => [
          qualified.fileId,
          qualifierOf(qualified),
        ]),
      );
    }
  }
}

/**
 * Display labels for a panel's series, keyed by series id.
 *
 * A series' label defaults to `topicName.messagePath`. When two or more series
 * in the panel display that same default (the same signal plotted from
 * different files, e.g. one per recording session), each is qualified with the
 * trailing segments of its source file's `relative_path`, starting at the
 * basename and growing only as far as needed to tell the files apart:
 * `/imu.accel.x (session_042.mcap)`. A series whose topic is not associated
 * with one of `files` keeps the bare default even when that default is
 * duplicated, since no path is available to name its source.
 *
 * An explicit `config.label` is displayed as written and never qualified. It
 * also does not count toward duplication: a default label shared only with
 * hand-labeled series is displayed by exactly one row, so it needs no
 * qualifier.
 */
export function seriesDisplayLabels(
  series: PlotSeries[],
  files: FileRecord[],
): Record<string, string> {
  const labels: Record<string, string> = {};
  const seriesByDefaultLabel = new Map<string, PlotSeries[]>();
  for (const seriesItem of series) {
    if (seriesItem.config?.label !== undefined) {
      labels[seriesItem.id] = seriesItem.config.label;
      continue;
    }
    const messagePath = MessagePathNode.partsToString(
      seriesItem.data.messagePath.parts,
    );
    const defaultLabel = `${seriesItem.data.topic.name}${PathParser.TOPIC_SEPARATOR}${messagePath}`;
    const group = seriesByDefaultLabel.get(defaultLabel);
    if (group === undefined) {
      seriesByDefaultLabel.set(defaultLabel, [seriesItem]);
    } else {
      group.push(seriesItem);
    }
  }

  const fileById = new Map(files.map((file) => [file.file_id, file]));
  const seriesFile = (seriesItem: PlotSeries): FileRecord | undefined => {
    const association = seriesItem.data.topic.association;
    return association.association_type === AssociationType.File
      ? fileById.get(association.association_id)
      : undefined;
  };

  for (const [defaultLabel, group] of seriesByDefaultLabel) {
    if (group.length === 1) {
      labels[group[0].id] = defaultLabel;
      continue;
    }

    const qualifierByFileId = distinguishingQualifiers(
      group.map(seriesFile).filter((file) => file !== undefined),
    );
    for (const seriesItem of group) {
      const file = seriesFile(seriesItem);
      const qualifier =
        file !== undefined ? qualifierByFileId.get(file.file_id) : undefined;
      labels[seriesItem.id] =
        qualifier === undefined
          ? defaultLabel
          : `${defaultLabel} (${qualifier})`;
    }
  }
  return labels;
}
